<script setup>
import AttachmentFiles from "../../Components/AttachmentFiles.vue";
import {useHttpErrorsHandler} from "../../Composables/httpErrorsHandler.js";
import {useRouter} from "vue-router";
import {computed, onBeforeMount, ref} from "vue";
import Loader from "../../Components/Loader.vue";
import {useToast} from "vue-toast-notification";
import Comments from "../../Components/Comments.vue";
const $toast = useToast();
const { httpErrorsHandler } = useHttpErrorsHandler();
const router = useRouter();
const loading = ref(true)
const errors = ref(null)
const helpArticle = ref(null)

const hasAttachments = computed(() => (helpArticle.value?.data.attachments?.length ?? 0) > 0)
// Même format que les vignettes de la liste (800×470), à la largeur d'affichage (960 px) et en double pour les écrans haute densité
const coverUrl = (width) => '/format-image/' + helpArticle.value.data.image + `?w=${width}&h=${Math.round(width * 470 / 800)}&fm=webp&q=85&fit=crop`
// Environ 200 mots par minute, au moins 1 minute
const readingTime = computed(() => Math.max(1, Math.round((helpArticle.value?.data.content_txt ?? '').split(/\s+/).filter(Boolean).length / 200)))
function getHelpArticle() {
    loading.value = true;
    axios.get("/api/help-articles/" + router.currentRoute.value.params.id)
        .then(response => {
            helpArticle.value = response.data;
            loading.value = false;
        })
        .catch(error => {
            httpErrorsHandler(error);
            loading.value = false;
        });
}
onBeforeMount(() => {
    getHelpArticle()
})

</script>

<template>
    <div class="row">
        <div class="col-12" v-show="loading">
            <loader />
        </div>
        <div class="col-12" v-if="!loading">
            <!-- Largeur limitée et centrée : lisible aussi sur les très grands écrans -->
            <div class="tab-content text-muted help-page" :class="{ 'is-narrow': !hasAttachments }">
                <div class="tab-pane fade show active" id="project-overview" role="tabpanel">
                    <div class="row">
                        <div :class="hasAttachments ? 'col-xxl-9' : 'col-12'">

                            <article class="card help-article">
                                <!-- Visuel entier au format des fiches d'aide (800×470), sur toute la largeur de l'article -->
                                <img v-if="helpArticle.data.image" :src="coverUrl(960)" :srcset="`${coverUrl(960)} 960w, ${coverUrl(1920)} 1920w`" sizes="(max-width: 1000px) 100vw, 960px" alt="" class="help-cover-image" width="800" height="470">

                                <header class="help-heading">
                                    <span class="badge bg-secondary-subtle text-secondary mb-2">{{ helpArticle.data.category.data.name }}</span>
                                    <h3 class="help-heading-title">{{ helpArticle.data.title }}</h3>
                                    <div class="help-heading-meta">
                                        <img :src="'/format-image/'+helpArticle.data.author.data.profile_picture+'?w=64&h=64&fm=webp&fit=crop'" alt="" class="avatar-xs rounded-circle">
                                        <span class="fw-medium text-body">{{ helpArticle.data.author.data.first_name }} {{ helpArticle.data.author.data.last_name }}</span>
                                        <!-- Chaque « · » reste avec l'élément qui le suit : jamais seul en fin de ligne -->
                                        <span class="text-nowrap"><span class="help-heading-dot">·</span> <span v-tooltip="helpArticle.data.updated_at_fr !== helpArticle.data.created_at_fr ? `Modifié le ${helpArticle.data.updated_at_fr}` : null">{{ helpArticle.data.created_at_fr }}</span></span>
                                        <span class="text-nowrap"><span class="help-heading-dot">·</span> {{ readingTime }} min de lecture</span>
                                        <router-link :to="{ name: 'help-articles.edit', params:{ id: helpArticle.data.id } }" class="btn btn-sm btn-soft-secondary ms-auto" v-if="helpArticle.can.update">
                                            <i class="ri-edit-2-fill me-1"></i> Modifier
                                        </router-link>
                                    </div>
                                </header>

                                <div class="card-body help-body">
                                    <div class="card-text text-body article-container" v-html="helpArticle.data.content"> </div>
                                </div>
                            </article>
                        </div>

                        <div class="col-xxl-3" v-if="hasAttachments">
                            <div class="card">
                                <div class="card-header align-items-center d-flex border-bottom-dashed">
                                    <h4 class="card-title mb-0 flex-grow-1"><i class="mdi mdi-attachment me-2 align-middle fs-4"></i>Fichiers joints</h4>
                                </div>
                                <div class="card-body">
                                    <attachment-files :attachments="helpArticle.data.attachments" stacked downloadable />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <comments
                    commentable-type="App\Models\HelpArticle"
                    :commentable-id="helpArticle.data.id"
                    :bs-class="hasAttachments ? 'col-xxl-9' : 'col-12'"
                />
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Largeur d'article de lecture : visuel, titre et texte alignés sur la même largeur */
.help-page {
    max-width: 1300px;
    margin: 0 auto;
}

/* Sans fichiers joints, l'article garde la même largeur qu'avec la colonne de droite */
.help-page.is-narrow {
    max-width: 960px;
}

.help-article {
    overflow: hidden;
}

/* Visuel sur toute la largeur de la carte : seul l'arrondi de la carte compte */
.help-cover-image {
    display: block;
    width: 100%;
    height: auto;
    aspect-ratio: 800 / 470;
    object-fit: cover;
}

.help-heading {
    margin: 0 2.25rem;
    padding: 1.5rem 0 1.25rem;
    border-bottom: 1px dashed var(--vz-border-color);
}

.help-heading-title {
    margin-bottom: 0.75rem;
    font-size: 1.375rem;
    font-weight: 600;
    line-height: 1.35;
}

.help-heading-meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.4rem;
    font-size: 0.8125rem;
    color: var(--vz-secondary-color);
}

.help-heading-dot {
    opacity: 0.6;
}

/* Texte : colonne de lecture confortable */
.help-body {
    padding: 1.5rem 2.25rem 2rem;
}

.help-body .article-container {
    font-size: 0.95rem;
    line-height: 1.7;
}

@media (max-width: 767.98px) {
    .help-heading {
        margin: 0 1.25rem;
    }

    .help-body {
        padding-right: 1.25rem;
        padding-left: 1.25rem;
    }
}
</style>
